import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'react-bootstrap';
import Loading from "./components/Loading";
import { getDefaultRoute } from './utils/history';
import history from './utils/history';
import { fetchMe } from './utils/staffApiService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const normalizePermissions = (permissions = []) =>
  (permissions || [])
    .map((permission) => {
      if (typeof permission === "string") return permission;
      if (permission && typeof permission.name === "string") return permission.name;
      return null;
    })
    .filter(Boolean);

const hasVenueAccess = (permissionNames = []) => {
  const hasPanelAccess = permissionNames.includes("access_venue_panel");
  const hasAnyOperationalPermission = permissionNames.some((permission) =>
    ["manage_shift", "view_orders", "edit_orders", "view_tables", "edit_tables", "view_requests", "edit_requests", "take_payment"].includes(permission)
  );
  return hasPanelAccess && hasAnyOperationalPermission;
};

const AuthenticatedApp = ({ children }) => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
  const [authContext, setAuthContext] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const deniedLogRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      if (!isLoading && !isAuthenticated) {
        try {
          await loginWithRedirect();
        } catch (err) {
          console.error("Redirect failed:", err);
        }
        return;
      }

      if (isAuthenticated && user && isMounted) {
        try {
          const me = await fetchMe(getAccessTokenSilently);
          const permissionNames = normalizePermissions(me?.permissions);
          const nextContext = {
            user,
            isAuthenticated,
            userRole: me?.role || null,
            userPermissions: permissionNames,
            venueId: me?.venueId ?? null,
            backendUser: me,
          };

          setAuthContext(nextContext);

          if (!hasVenueAccess(permissionNames)) {
            setAccessDenied(true);
            if (!deniedLogRef.current) {
              console.group("[Venue Auth] Access denied diagnostics");
              console.log("backend_context", me);
              console.log("permission_names", permissionNames);
              console.log("required", {
                panel: "access_venue_panel",
                anyOf: ["manage_shift", "view_orders", "edit_orders", "view_tables", "edit_tables", "view_requests", "edit_requests", "take_payment"],
              });
              console.groupEnd();
              deniedLogRef.current = true;
            }
            setIsProcessing(false);
            return;
          }

          deniedLogRef.current = false;
          setAccessDenied(false);

          const defaultRoute = getDefaultRoute(permissionNames);
          if (history.location.pathname === "/") {
            history.push(defaultRoute);
          }
          setIsProcessing(false);
        } catch (err) {
          console.error("Failed to load backend auth context:", err);
          setAccessDenied(true);
          setIsProcessing(false);
        }
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, [isLoading, isAuthenticated, user, loginWithRedirect, getAccessTokenSilently]);

  if (isLoading || isProcessing) {
    return <Loading />;
  }

  if (accessDenied || !authContext || !hasVenueAccess(authContext.userPermissions || [])) {
    return (
      <div className="venue-shell">
        <main className="venue-shell-main">
          <div className="venue-app-content">
            <section
              className="venue-surface-card venue-surface-card--padded text-center mx-auto"
              style={{ maxWidth: 560 }}
            >
              <div className="venue-page-eyebrow">Erişim</div>
              <h3 className="venue-page-title mb-3">Bu hesap venue paneline yetkili değil</h3>
              <p className="venue-page-subtitle mb-4">
                Venue paneline devam etmek için işletme yetkisi olan bir hesapla giriş yapın.
              </p>
              <Button
                variant="outline-dark"
                className="px-4"
                onClick={() =>
                  logout({
                    logoutParams: {
                      returnTo: window.location.origin,
                    },
                  })
                }
              >
                Çıkış yap
              </Button>
            </section>
          </div>
        </main>
      </div>
    );
  }

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

export default AuthenticatedApp;
